import React, { useState } from 'react';
import moment from 'moment';
import { IoClose, IoPencil, IoTrash, IoWarningOutline, IoCheckmarkCircleOutline } from 'react-icons/io5';
import EditarTransaccionModal from './EditarTransaccionModal';
import ExportButton from '../common/ExportButton';
import { exportToCSV, exportToExcel, exportToPDF } from '../../utils/exportUtils';
import { db } from '../../firebaseConfig';
import { doc, deleteDoc, updateDoc, query, collection, where, getDocs, getDoc } from 'firebase/firestore';

function DetalleCuentaModal({ account, entries, onClose }) {
    // State stores object: { id: '...', type: '...' } or null
    const [editingRef, setEditingRef] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null); // { id, type }
    const [successMessage, setSuccessMessage] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [inspectedDoc, setInspectedDoc] = useState(null);
    const [inspectingLoading, setInspectingLoading] = useState(false);

    const handleRowClick = async (mov) => {
        if (!mov.referenciaId || !mov.tipoReferencia) return;
        setInspectingLoading(true);
        try {
            let collectionName = '';
            if (mov.tipoReferencia === 'CARGO') {
                collectionName = 'cargos';
            } else if (['GASTO', 'GASTO_PROVISION', 'INGRESO_CLASIFICADO'].includes(mov.tipoReferencia)) {
                collectionName = 'gastos';
            } else if (['PAGO_PROVEEDOR', 'PAGO', 'COBRO'].includes(mov.tipoReferencia)) {
                collectionName = 'movimientos_bancarios';
            }

            if (!collectionName) return;

            const docRef = doc(db, collectionName, mov.referenciaId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setInspectedDoc({
                    tipoRef: mov.tipoReferencia,
                    data: docSnap.data()
                });
            } else {
                alert("El documento original no fue encontrado. Posiblemente fue eliminado.");
            }
        } catch (e) {
            console.error(e);
            alert("Error al cargar el documento origen: " + e.message);
        } finally {
            setInspectingLoading(false);
        }
    };

    const handleEditSuccess = () => {
        setEditingRef(null);
        setSuccessMessage("Registro actualizado. Por favor haz clic en 'Actualizar Datos' para refrescar el reporte.");
    };

    const executeDelete = async () => {
        if (!deleteTarget) return;
        const { id, type } = deleteTarget;
        setDeleteTarget(null);

        try {
            // 1. Delete associated Ledger Entries from correct collection (contabilidad_asientos)
            const qLedger = query(collection(db, "contabilidad_asientos"), where("referenciaId", "==", id));
            const snap = await getDocs(qLedger);
            const deletePromises = snap.docs.map(d => deleteDoc(d.ref));
            await Promise.all(deletePromises);

            // 2. Handle Source Document
            if (type === 'GASTO_PROVISION' || type === 'GASTO') {
                const gastoRef = doc(db, "gastos", id);
                await deleteDoc(gastoRef);
            }
            else if (type === 'PAGO_PROVEEDOR' || type === 'INGRESO_CLASIFICADO' || type === 'PAGO' || type === 'COBRO') {
                // Revert Bank Movement to 'pendiente'
                const movRef = doc(db, "movimientos_bancarios", id);
                await updateDoc(movRef, {
                    estatus: 'pendiente',
                    gastoId: null,
                    tipoClasificacion: null,
                    categoria: null
                });
            }

            setSuccessMessage("Registro contable y asiento eliminados correctamente.");
        } catch (e) {
            console.error(e);
            setErrorMessage("Error al eliminar: " + e.message);
        }
    };

    if (!account) return null;

    // Sorting entries by date
    const sortedEntries = [...entries].sort((a, b) => a.fecha.toMillis() - b.fecha.toMillis());

    // Calculate Running Balance
    let runningBalance = 0;
    const isDeudora = account.naturaleza === 'DEUDORA';

    // Export Handlers
    const getExportData = () => {
        let balance = 0;
        return sortedEntries.map(e => {
            const debe = e.debe || 0;
            const haber = e.haber || 0;
            if (isDeudora) balance += (debe - haber);
            else balance += (haber - debe);

            return {
                Fecha: moment(e.fecha?.toDate ? e.fecha.toDate() : (e.fecha || new Date())).format('DD/MM/YYYY'),
                Concepto: e.concepto,
                Referencia: `${e.tipoReferencia || ''} ${e.referenciaId || ''}`,
                Debe: debe,
                Haber: haber,
                Saldo: balance
            };
        });
    };

    const handleExportCSV = () => {
        exportToCSV(getExportData(), `Detalle_${account.numero}`);
    };

    const handleExportExcel = () => {
        exportToExcel(getExportData(), `Detalle_${account.numero}`, 'Movimientos');
    };

    const handleExportPDF = () => {
        const columns = ['Fecha', 'Concepto', 'Ref', 'Debe', 'Haber', 'Saldo'];
        const rows = getExportData().map(item => [
            item.Fecha,
            item.Concepto,
            item.Referencia,
            `$${item.Debe.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
            `$${item.Haber.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
            `$${item.Saldo.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
        ]);

        exportToPDF(
            `Auxiliar de Cuenta: ${account.numero} - ${account.nombre}`,
            columns,
            rows,
            `Detalle_${account.numero}`
        );
    };

    return (
        <div className="modal-overlay" style={{ zIndex: 999 }}>
            {editingRef && (
                <EditarTransaccionModal
                    id={editingRef.id}
                    type={editingRef.type}
                    onClose={() => setEditingRef(null)}
                    onSuccess={handleEditSuccess}
                />
            )}

            {deleteTarget && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-content" style={{ maxWidth: '420px', textAlign: 'center', padding: '30px' }}>
                        <IoWarningOutline size={60} color="#f59e0b" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem', fontWeight: 'bold' }}>
                            ¿Eliminar Registro Contable?
                        </h3>
                        <p style={{ color: '#4b5563', marginBottom: '25px', fontSize: '0.95rem', lineHeight: '1.5' }}>
                            ¿Estás seguro de eliminar este registro contable? Si es una factura, se borrará definitivamente. Si es un pago/cobro, volverá a pendientes.
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button 
                                className="btn-cancel" 
                                style={{ flex: 1, padding: '12px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', borderRadius: '10px' }} 
                                onClick={() => setDeleteTarget(null)}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn-confirm" 
                                style={{ flex: 1, padding: '12px', fontSize: '1rem', fontWeight: 'bold', background: '#ef4444', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '10px' }} 
                                onClick={executeDelete}
                            >
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {successMessage && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
                        <IoCheckmarkCircleOutline size={60} color="#10b981" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem', fontWeight: 'bold' }}>
                            Operación Exitosa
                        </h3>
                        <p style={{ color: '#4b5563', marginBottom: '20px', fontSize: '0.95rem' }}>
                            {successMessage}
                        </p>
                        <button 
                            className="btn-confirm" 
                            style={{ width: '100%', padding: '12px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', borderRadius: '10px' }} 
                            onClick={() => {
                                setSuccessMessage(null);
                                onClose();
                            }}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}

            {errorMessage && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
                        <IoWarningOutline size={60} color="#ef4444" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem', fontWeight: 'bold' }}>
                            Error
                        </h3>
                        <p style={{ color: '#4b5563', marginBottom: '20px', fontSize: '0.95rem' }}>
                            {errorMessage}
                        </p>
                        <button 
                            className="btn-confirm" 
                            style={{ width: '100%', padding: '12px', fontSize: '1rem', fontWeight: 'bold', background: '#ef4444', cursor: 'pointer', borderRadius: '10px' }} 
                            onClick={() => setErrorMessage(null)}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            )}
            {inspectingLoading && (
                <div className="modal-overlay" style={{ zIndex: 3000 }}>
                    <div className="modal-content" style={{ maxWidth: '300px', textAlign: 'center', padding: '20px' }}>
                        <p style={{ fontWeight: 'bold' }}>Cargando documento fuente original...</p>
                    </div>
                </div>
            )}

            {inspectedDoc && (
                <div className="modal-overlay" style={{ zIndex: 3000 }}>
                    <div className="modal-content" style={{ maxWidth: '500px', width: '90%', padding: '25px', borderRadius: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
                            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.2rem', fontWeight: 'bold' }}>
                                Detalle del Documento Fuente ({inspectedDoc.tipoRef})
                            </h3>
                            <button onClick={() => setInspectedDoc(null)} className="close-button" style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}><IoClose size={20} /></button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '0.95rem', textAlign: 'left' }}>
                            {inspectedDoc.tipoRef === 'CARGO' && (
                                <>
                                    <div><strong>Concepto/Descripción:</strong> {inspectedDoc.data.descripcion || inspectedDoc.data.concepto || '-'}</div>
                                    <div><strong>Tipo de Cargo:</strong> {inspectedDoc.data.tipo || '-'}</div>
                                    <div><strong>Monto:</strong> ${Number(inspectedDoc.data.monto).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                                    <div><strong>Residente:</strong> {inspectedDoc.data.nombreResidente || inspectedDoc.data.residenteNombre || '-'}</div>
                                    <div><strong>Unidad/Casa:</strong> {inspectedDoc.data.unidadResidente || inspectedDoc.data.unidad || '-'}</div>
                                    <div><strong>Vencimiento:</strong> {inspectedDoc.data.fechaVencimiento ? moment(inspectedDoc.data.fechaVencimiento.toDate ? inspectedDoc.data.fechaVencimiento.toDate() : inspectedDoc.data.fechaVencimiento).format('DD/MM/YYYY') : '-'}</div>
                                    <div><strong>Estatus:</strong> <span style={{ textTransform: 'capitalize', fontWeight: 'bold', color: inspectedDoc.data.estatus?.toLowerCase() === 'pagado' ? '#10b981' : '#f59e0b' }}>{inspectedDoc.data.estatus || 'pendiente'}</span></div>
                                </>
                            )}
                            {['GASTO', 'GASTO_PROVISION', 'INGRESO_CLASIFICADO'].includes(inspectedDoc.tipoRef) && (
                                <>
                                    <div><strong>Descripción:</strong> {inspectedDoc.data.descripcion || '-'}</div>
                                    <div><strong>Categoría:</strong> {inspectedDoc.data.categoria || '-'}</div>
                                    <div><strong>Monto:</strong> ${Number(inspectedDoc.data.monto).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                                    <div><strong>Proveedor:</strong> {inspectedDoc.data.proveedor || '-'}</div>
                                    <div><strong>Tipo:</strong> <span style={{ textTransform: 'capitalize' }}>{inspectedDoc.data.tipo || 'egreso'}</span></div>
                                    <div><strong>Fecha:</strong> {inspectedDoc.data.fecha ? moment(inspectedDoc.data.fecha.toDate ? inspectedDoc.data.fecha.toDate() : inspectedDoc.data.fecha).format('DD/MM/YYYY') : '-'}</div>
                                    {inspectedDoc.data.archivoUrl && (
                                        <div>
                                            <strong>Factura/Comprobante:</strong>{' '}
                                            <a href={inspectedDoc.data.archivoUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline', fontWeight: '600' }}>
                                                Ver Comprobante Adjunto
                                            </a>
                                        </div>
                                    )}
                                </>
                            )}
                            {['PAGO_PROVEEDOR', 'PAGO', 'COBRO'].includes(inspectedDoc.tipoRef) && (
                                <>
                                    <div><strong>Descripción del Banco:</strong> {inspectedDoc.data.descripcion || '-'}</div>
                                    <div><strong>Monto Bancario:</strong> ${Number(inspectedDoc.data.monto).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                                    <div><strong>Fecha de Movimiento:</strong> {inspectedDoc.data.fecha ? moment(inspectedDoc.data.fecha.toDate ? inspectedDoc.data.fecha.toDate() : inspectedDoc.data.fecha).format('DD/MM/YYYY') : '-'}</div>
                                    <div><strong>Referencia del Banco:</strong> {inspectedDoc.data.referencia || '-'}</div>
                                    <div><strong>Estatus de Conciliación:</strong> <span style={{ textTransform: 'capitalize', fontWeight: 'bold', color: '#10b981' }}>{inspectedDoc.data.estatus || 'conciliado'}</span></div>
                                </>
                            )}
                        </div>
                        <div style={{ marginTop: '25px', textAlign: 'right' }}>
                            <button className="btn-confirm" style={{ padding: '8px 20px', borderRadius: '8px' }} onClick={() => setInspectedDoc(null)}>
                                Cerrar Detalle
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="modal-content" style={{ maxWidth: '800px', width: '90%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
                <div className="modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <h2 style={{ margin: 0 }}>Detalle: {account.numero} - {account.nombre}</h2>
                        <ExportButton
                            onExportCSV={handleExportCSV}
                            onExportExcel={handleExportExcel}
                            onExportPDF={handleExportPDF}
                        />
                    </div>
                    <button onClick={onClose} className="close-button"><IoClose /></button>
                </div>

                <div className="modal-body" style={{ overflowY: 'auto', padding: '0' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
                        <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                            <tr>
                                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Fecha</th>
                                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Concepto</th>
                                <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Debe</th>
                                <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Haber</th>
                                <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Saldo</th>
                                <th style={{ padding: '10px', width: '50px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedEntries.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                                        No hay movimientos en este periodo.
                                    </td>
                                </tr>
                            ) : (
                                sortedEntries.map((mov, idx) => {
                                    const debe = mov.debe || 0;
                                    const haber = mov.haber || 0;

                                    if (isDeudora) {
                                        runningBalance += (debe - haber);
                                    } else {
                                        runningBalance += (haber - debe);
                                    }

                                    const validTypes = ['GASTO_PROVISION', 'PAGO_PROVEEDOR', 'INGRESO_CLASIFICADO'];
                                    const canEdit = validTypes.includes(mov.tipoReferencia) && mov.referenciaId;

                                    return (
                                        <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={{ padding: '8px' }}>
                                                {moment(mov.fecha?.toDate ? mov.fecha.toDate() : (mov.fecha || new Date())).format('DD/MM/YYYY')}
                                            </td>
                                            <td 
                                                style={{ padding: '8px', cursor: mov.referenciaId ? 'pointer' : 'default' }}
                                                onClick={() => mov.referenciaId && handleRowClick(mov)}
                                                title={mov.referenciaId ? "Haga clic para ver el documento fuente original" : ""}
                                            >
                                                <span style={{ 
                                                    fontWeight: mov.referenciaId ? '600' : 'normal', 
                                                    color: mov.referenciaId ? '#1e40af' : 'inherit',
                                                    textDecoration: mov.referenciaId ? 'underline' : 'none'
                                                }}>
                                                    {mov.concepto}
                                                </span>
                                                {mov.metadata?.unidad && (
                                                    <span style={{ display: 'block', fontSize: '0.85em', color: '#4b5563', marginTop: '2px', fontWeight: '500' }}>
                                                        🏠 {mov.metadata.unidad} {mov.metadata.residenteNombre ? `- ${mov.metadata.residenteNombre}` : ''}
                                                    </span>
                                                )}
                                                {mov.metadata?.proveedor && (
                                                    <span style={{ display: 'block', fontSize: '0.85em', color: '#4b5563', marginTop: '2px', fontWeight: '500' }}>
                                                        🏢 Proveedor: {mov.metadata.proveedor}
                                                    </span>
                                                )}
                                                {mov.tipoReferencia &&
                                                    <span style={{ display: 'block', fontSize: '0.8em', color: '#888', marginTop: '2px' }}>
                                                        Ref: {mov.tipoReferencia}
                                                    </span>
                                                }
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'right', color: debe > 0 ? '#333' : '#ccc' }}>
                                                {debe > 0 ? `$${Number(debe).toLocaleString('en-US', { minimumFractionDigits: 2 })} ` : '-'}
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'right', color: haber > 0 ? '#333' : '#ccc' }}>
                                                {haber > 0 ? `$${Number(haber).toLocaleString('en-US', { minimumFractionDigits: 2 })} ` : '-'}
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                                                {`$${Number(runningBalance).toLocaleString('en-US', { minimumFractionDigits: 2 })} `}
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                {canEdit && (
                                                    <>
                                                        <button
                                                            className="small-action-btn edit"
                                                            onClick={() => setEditingRef({ id: mov.referenciaId, type: mov.tipoReferencia })}
                                                            title="Editar Fecha/Registro"
                                                            style={{ cursor: 'pointer', border: 'none', background: 'transparent', color: '#0d6efd', marginRight: '5px' }}
                                                        >
                                                            <IoPencil />
                                                        </button>
                                                        <button
                                                            className="small-action-btn delete"
                                                            onClick={() => setDeleteTarget({ id: mov.referenciaId, type: mov.tipoReferencia })}
                                                            title="Eliminar registro"
                                                            style={{ cursor: 'pointer', border: 'none', background: 'transparent', color: '#dc2626' }}
                                                        >
                                                            <IoTrash />
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="modal-footer" style={{ marginTop: 'auto', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                    <div style={{ textAlign: 'right', fontSize: '1.1em', fontWeight: 'bold' }}>
                        Saldo Final: ${runningBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DetalleCuentaModal;
